import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/layout/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Guests from "@/pages/Guests";
import GuestDetail from "@/pages/GuestDetail";
import EmailTemplates from "@/pages/EmailTemplates";
import Sponsors from "@/pages/Sponsors";
import ShowSettings from "@/pages/ShowSettings";
import GettingStarted from "@/pages/GettingStarted";
import AllShows from "@/pages/AllShows";
import UserAccounts from "@/pages/UserAccounts";
import AccessRequests from "@/pages/AccessRequests";
import PlatformDashboard from "@/pages/PlatformDashboard";
import IntakeForm from "@/pages/IntakeForm";
import Confirm from "@/pages/Confirm";
import PromoKit from "@/pages/PromoKit";

function RequireAuth({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_reset_password) return <Navigate to="/change-password" replace />;
  return children;
}

function RequireSuperAdmin({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_reset_password) return <Navigate to="/change-password" replace />;
  if (user.role !== "super_admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_reset_password) return <Navigate to="/change-password" replace />;
  if (user.role === "super_admin") return <Navigate to="/admin/platform" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/intake/:token" element={<IntakeForm />} />
          <Route path="/confirm/:token" element={<Confirm />} />
          <Route path="/promo-kit/:token" element={<PromoKit />} />
          <Route element={<RequireAuth><Layout /></RequireAuth>}>
            <Route path="/dashboard" element={<RequireAdmin><Dashboard /></RequireAdmin>} />
            <Route path="/guests" element={<RequireAdmin><Guests /></RequireAdmin>} />
            <Route path="/guests/:id" element={<RequireAdmin><GuestDetail /></RequireAdmin>} />
            <Route path="/email-templates" element={<RequireAdmin><EmailTemplates /></RequireAdmin>} />
            <Route path="/sponsors" element={<RequireAdmin><Sponsors /></RequireAdmin>} />
            <Route path="/show-settings" element={<RequireAdmin><ShowSettings /></RequireAdmin>} />
            <Route path="/help" element={<RequireAdmin><GettingStarted /></RequireAdmin>} />
            <Route path="/admin/platform" element={<RequireSuperAdmin><PlatformDashboard /></RequireSuperAdmin>} />
            <Route path="/admin/shows" element={<RequireSuperAdmin><AllShows /></RequireSuperAdmin>} />
            <Route path="/admin/users" element={<RequireSuperAdmin><UserAccounts /></RequireSuperAdmin>} />
            <Route path="/admin/access-requests" element={<RequireSuperAdmin><AccessRequests /></RequireSuperAdmin>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
